import { FC, useEffect, useState } from 'react';
import styles from './ExternalAccount.module.css';
import { useSelector } from 'react-redux';
import { useDeleteExternalAccountMutation } from '../../redux/services/users';
import { TUser } from '../../redux/services/client';
import { BACKEND_URL, GRAVATAR_URL } from '../../constants';
import { getSHA256Hash, getExternalAccountLabel } from '../../helpers';
import { EClaimPrivacy, PublicStatusPopover } from './PublicStatusPopover';
import clsx from 'clsx';
import Button from '@mui/material/Button';
import { ReactComponent as IdIcon } from '../../icons/Id.svg';
import { ReactComponent as LdapIcon } from '../../icons/Ldap.svg';
import { ReactComponent as MailIcon } from '../../icons/Mail.svg';
import { ReactComponent as _1CIcon } from '../../icons/1c.svg';
import { ReactComponent as IdmIcon } from '../../icons/Idm.svg';
import aldProIcon from '../../icons/ald-pro.png';
import Jazzicon, { jsNumberForAddress } from 'react-jazzicon';
import { ExternalAccountActionPopover } from './ExternalAccountActionPopover';
import { CustomTypography } from '../custom/CustomTypography';
import { useNavigate, useParams } from 'react-router-dom';
import { subTabs, tabs } from 'src/enums';
import { RootState } from 'src/redux/store';
import { IExternalAccount, IUserProfile } from 'src/redux/services/users';
import { ProviderType } from 'src/redux/services/provider';
import { addMethodsType, addProviderType } from '../extensions/Settings';
import { useSetMainEmailMutation } from 'src/redux/services/user';

type ExternalAccountProps = {
  account: Omit<IExternalAccount, 'user' | 'user_id'>;
  userProfile: IUserProfile | Partial<TUser>;
  public_accounts_claims_oauth: number[] | undefined;
  public_accounts_claims_gravatar: number[] | undefined;
  withoutButtons?: boolean;
};

export const ExternalAccount: FC<ExternalAccountProps> = ({
  account,
  userProfile: { email: mainEmail, id: userId },
  public_accounts_claims_oauth,
  public_accounts_claims_gravatar,
  withoutButtons = false,
}) => {
  const navigate = useNavigate();
  const { appId = '', clientId = '' } = useParams<{ appId: string; clientId: string }>();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [gravatarAvatar, setGravatarAvatar] = useState<string | null>(null);
  const [deleteExternalAccount] = useDeleteExternalAccountMutation();
  const [setMainEmail] = useSetMainEmailMutation();
  const startRoutePath = useSelector((state: RootState) => state.app.startRoutePath);

  useEffect(() => {
    if (account.type === ProviderType.EMAIL) setEmailAvatarFromGravatar(account.sub as string);
  }, []);

  const setEmailAvatarFromGravatar = async (email: string) => {
    const hash = await getSHA256Hash(email);
    return setGravatarAvatar(`${GRAVATAR_URL}/${hash}?s=${40}&d=404&r=g`);
  };

  //TODO вынести платные иконки в отдельный компонент
  const getAvatar = () => {
    if (account.avatar)
      return (
        <img className={styles['provider-icon']} src={account.avatar} width="40" height="40" />
      );

    switch (account.type) {
      case ProviderType.EMAIL:
        return gravatarAvatar ? (
          <img
            onError={() => {
              setGravatarAvatar(null);
            }}
            className={styles['provider-icon']}
            src={gravatarAvatar}
            width="40"
            height="40"
          />
        ) : (
          <MailIcon />
        );
      case ProviderType.SMS:
        return <AccountTypesBlock pathToImage="/public/default/sms.svg" />;
      case ProviderType.KLOUD:
        return <AccountTypesBlock pathToImage="/public/default/kloud.svg" />;
      case ProviderType.YANDEX:
        return <AccountTypesBlock pathToImage="/public/default/yandex.svg" />;
      case ProviderType.VK:
        return <AccountTypesBlock pathToImage="/public/default/vk.svg" />;
      case ProviderType.GOOGLE:
        return <AccountTypesBlock pathToImage="/public/default/google.svg" />;
      case ProviderType.MAILRU:
        return <AccountTypesBlock pathToImage="/public/default/mail.svg" />;
      case ProviderType.ETHEREUM:
        return <Jazzicon diameter={40} seed={jsNumberForAddress(account.sub as string)} />;

      case addMethodsType.QR_CODE:
        return <AccountTypesBlock pathToImage="/public/default/qrcode.svg" />;
      case addMethodsType.ESIA:
        return <AccountTypesBlock pathToImage="/public/default/esia.svg" />;
      case addProviderType.LDAP:
        return <LdapIcon />;
      case addProviderType.ALDPRO:
        return (
          <div
            style={{
              backgroundImage: `url(${aldProIcon})`,
              backgroundSize: 'cover',
              width: 38,
              height: 38,
            }}
          />
        );
      case addProviderType._1C:
        return <_1CIcon />;
      case addProviderType.IDM:
        return <IdmIcon width={40} height={40} />;
      default:
        return <IdIcon />;
    }
  };

  const historyPush = () => {
    if (clientId)
      navigate(
        `/${startRoutePath}/${appId}/${tabs.clients}/${clientId}/${tabs.users}/${userId}/${subTabs.edit}`,
      );
    if (appId) {
      navigate(`/${startRoutePath}/${appId}/${tabs.users}/${userId}/${subTabs.edit}`);
    }
  };

  const handleDeleteClick = async () => {
    await deleteExternalAccount({ userId: userId as number, accountId: account.id });
    setAnchorEl(null);
  };

  const handleSetMainEmail = async (email: string) => {
    await setMainEmail({ email, userId: String(userId) });
    setAnchorEl(null);
  };

  const getExternakAccountClaimPrivacy = () => {
    if (public_accounts_claims_gravatar?.includes(parseInt(account.id, 10)))
      return EClaimPrivacy.publicGravatar;
    if (public_accounts_claims_oauth?.includes(parseInt(account.id, 10)))
      return EClaimPrivacy.publicOauth;
    return EClaimPrivacy.private;
  };

  return (
    <div className={styles['info-item']} style={{ justifyContent: 'space-between' }}>
      <div style={{ display: 'flex', width: withoutButtons ? '95%' : 'calc(100% - 119px)' }}>
        <div className={styles['provider-icon']}>{getAvatar()}</div>
        <div className={styles['provider-info']}>
          <CustomTypography className={clsx('text-14', styles.ellipsis)}>
            {getExternalAccountLabel(account)}
          </CustomTypography>
          <div style={{ display: 'flex' }}>
            <CustomTypography className={clsx('text-12')} color="grey">
              {account.type}
            </CustomTypography>
            {account.profile_link && (
              <CustomTypography style={{ marginLeft: 16 }} className={clsx('text-12')} color="link">
                {account.profile_link}
              </CustomTypography>
            )}
          </div>
        </div>
      </div>
      {!!(public_accounts_claims_oauth && public_accounts_claims_oauth) && (
        <div style={{ display: 'flex' }}>
          {!withoutButtons && (
            <ExternalAccountActionPopover anchorEl={anchorEl} setAnchorEl={setAnchorEl}>
              <Button
                className={clsx(styles['popover-button'])}
                onClick={() => {
                  account.type === ProviderType.EMAIL && account.sub === mainEmail
                    ? historyPush()
                    : handleDeleteClick();
                }}
              >
                <CustomTypography className={clsx('text-14')}>
                  {account.type === ProviderType.EMAIL && account.sub === mainEmail
                    ? 'Изменить'
                    : 'Удалить'}
                </CustomTypography>
              </Button>
              {account.type === ProviderType.EMAIL && account.sub !== mainEmail && (
                <Button
                  className={clsx(styles['popover-button'])}
                  onClick={() => {
                    if (account.sub) handleSetMainEmail(account.sub);
                  }}
                >
                  <CustomTypography className={clsx('text-14')}>Сделать основной</CustomTypography>
                </Button>
              )}
            </ExternalAccountActionPopover>
          )}
          <PublicStatusPopover
            userId={String(userId)}
            claimPrivacy={getExternakAccountClaimPrivacy()}
            externalAccountId={parseInt(account.id, 10)}
          />
        </div>
      )}
    </div>
  );
};

type AccountTypesProps = {
  pathToImage: string;
};

const AccountTypesBlock: FC<AccountTypesProps> = ({ pathToImage }) => {
  return (
    <div
      style={{
        backgroundImage: `url(${BACKEND_URL + pathToImage})`,
      }}
      className={styles['provider-icon-wrapper']}
    />
  );
};
